import { type Attachment, AttachmentScanStatus } from "@repo/api";
import { LightMode } from "@repo/skapa";
import { t } from "@repo/translation";
import { useCoWorkerName } from "@repo/ui";

import { type FileLoadingEnum } from "../utils";
import { FileListItem } from "./FileListItem";

export const ProjectFileItem = ({
  file,
  loading,
  loadingFileId,
  editable,
  onDownload,
  onDelete,
  onShare,
  imageUrls,
}: {
  file: Attachment;
  loading?: FileLoadingEnum;
  loadingFileId?: number;
  editable?: boolean;
  onDownload: (
    name: string,
    attachmentId: number,
    action: "OPEN" | "DOWNLOAD",
    mimeType: string,
    url?: string
  ) => void;
  onDelete?: (attachmentId: number) => void;
  imageUrls?: Record<string, string>;
  onShare?: (attachmentId: number, shared: boolean) => void;
}) => {
  const { name: coworkerName } = useCoWorkerName(file.uploaded_user);

  const isWaitingForScan =
    file.scanStatus === AttachmentScanStatus.WAITING_FOR_SCAN;
  const isInfected = file.scanStatus === AttachmentScanStatus.INFECTED;
  const isAccessBlocked = isWaitingForScan || isInfected;

  const getScanStatus = () => {
    if (isWaitingForScan) {
      return { text: t("label.attachment_pending") };
    }
    if (isInfected) {
      return {
        text: t("label.attachment_rejected"),
        color: LightMode.colourSemanticNegative,
      };
    }
    return null;
  };

  return (
    <FileListItem
      attachmentId={file.attachmentId}
      fileName={file.name}
      fileType={file.type}
      uploadedDate={file.uploaded_date}
      uploadedBy={coworkerName}
      status={getScanStatus()}
      editable={editable}
      loading={loading}
      loadingFileId={loadingFileId}
      onDelete={onDelete}
      onDownload={
        isAccessBlocked
          ? undefined
          : (action) =>
              onDownload(
                file.name,
                file.attachmentId,
                action,
                file.type ?? "application/octet-stream",
                imageUrls?.[file.name]
              )
      }
      allowPreview={!isAccessBlocked}
      onShare={onShare}
      isShared={file.isSharedWithCustomerProject}
    />
  );
};
